import getEligibleOrderBumpOffers, {
  BUMP_QUANTITY,
  getMaxBumpQuantity,
} from './getEligibleOrderBumpOffers';

/**
 * The bump selections a checkout settles on, worked out in one go.
 *
 * Ticked bumps that are not eligible are cleared (removed, not zeroed: a 0 is
 * the buyer's own "no"), eligible preselected bumps with no answer are ticked,
 * and quantities over a bump's ceiling are clamped. Each of those can change
 * what another bump's rule sees, so it repeats until nothing changes. Done
 * render by render instead, every step was painted: preselected bumps flashed
 * on load and the total jumped.
 *
 * Returns `orderBumps` itself when nothing changes. `context` is what
 * getEligibleOrderBumpOffers takes, bar `orderBumps`.
 */
export default function settleOrderBumps(orderBumps, context) {
  const initial = orderBumps ?? {};
  let current = initial;

  // Rules off a loop settle a step per level of a chain, so this is only a guard.
  for (let pass = 0; pass < 100; pass += 1) {
    const eligible = getEligibleOrderBumpOffers({ ...context, orderBumps: current });
    const eligibleIds = new Set(eligible.map(offer => String(offer.productId)));
    const next = { ...current };

    Object.entries(current).forEach(([productId, quantity]) => {
      if (quantity > 0 && !eligibleIds.has(productId)) delete next[productId];
    });

    eligible.forEach(offer => {
      const productId = String(offer.productId);
      if (offer.preselected && next[productId] === undefined) next[productId] = BUMP_QUANTITY;

      const ceiling = getMaxBumpQuantity(offer);
      if (next[productId] > ceiling) next[productId] = ceiling;
    });

    const isUnchanged =
      Object.keys(next).length === Object.keys(current).length &&
      Object.entries(next).every(([productId, quantity]) => current[productId] === quantity);
    if (isUnchanged) break;

    current = next;
  }

  return current === initial ? orderBumps : current;
}
